import React, { useEffect, useState } from 'react';
import { Sparkle, X, House, Buildings, SignIn, Check, CaretRight, Wrench } from '@phosphor-icons/react';
import { cn, getSession, setSession, clearSession, seedL8DemoFor, Session, getVerificationStates, setVerificationState, type VerificationStatus } from '../../lib/utils';
import { PAGE_ROUTE } from '../../lib/routes';
/**
 * Prototype-only floating switcher that lets you hop between pre-configured
 * demo accounts without going through the auth flow. Visible on every page
 * except fullscreen pages (Onboarding, Review submission).
 *
 * Mount once at the App root. Calls `navigate()` after switching so the
 * session state in App.tsx is re-read and the user lands somewhere sensible.
 */
type PersonaId = 'olivia-chirias' | 'andrei-chirias' | 'maria-chirias' | 'sara-combo' | 'andrei-proprietar' | 'maria-agent' | 'george-agent';
interface Persona {
  id: PersonaId;
  name: string;
  role: 'demand' | 'supply';
  roleLabel: string;
  hint: string;
  avatar: string;
  destination: string;
  session: Session;
}
const PERSONAS: Persona[] = [{
  id: 'olivia-chirias',
  name: 'Olivia P.',
  role: 'demand',
  roleLabel: 'Chiriaș · închiriat',
  hint: 'Sesiune închisă · 3 oferte arhivate',
  avatar: 'https://i.pravatar.cc/150?img=5',
  destination: PAGE_ROUTE.DASHBOARD_ANUNT,
  session: {
    name: 'Olivia P.',
    email: 'olivia.p@izio.test',
    phone: '0722 100 200',
    avatar: 'https://i.pravatar.cc/150?img=5',
    role: 'demand',
    hasListing: true,
    profileStatus: 'active',
    slug: 'olivia-p',
    memberSince: 2026
  }
}, {
  id: 'andrei-chirias',
  name: 'Andrei P.',
  role: 'demand',
  roleLabel: 'Chiriaș · activ',
  hint: 'Anunț activ · 3 oferte · 1 închiriere în istoric',
  avatar: 'https://i.pravatar.cc/150?img=11',
  destination: PAGE_ROUTE.DASHBOARD_PROFIL,
  session: {
    name: 'Andrei P.',
    email: 'andrei.p@izio.test',
    phone: '0733 100 200',
    avatar: 'https://i.pravatar.cc/150?img=11',
    role: 'demand',
    hasListing: true,
    profileStatus: 'active',
    slug: 'andrei-popescu',
    memberSince: 2026
  }
}, {
  id: 'maria-chirias',
  name: 'Maria I.',
  role: 'demand',
  roleLabel: 'Chiriaș',
  hint: 'Onboarding · fără anunț încă',
  avatar: 'https://i.pravatar.cc/150?img=9',
  destination: PAGE_ROUTE.DASHBOARD_PROFIL,
  session: {
    name: 'Maria I.',
    email: 'maria.i@izio.test',
    avatar: 'https://i.pravatar.cc/150?img=9',
    role: 'demand',
    hasListing: false,
    profileStatus: 'active',
    memberSince: 2026
  }
}, {
  id: 'sara-combo',
  name: 'Sara V.',
  role: 'demand',
  roleLabel: 'Chiriaș · grup',
  hint: 'Grup de 3 · 1 închiriere în istoric',
  avatar: 'https://i.pravatar.cc/150?img=47',
  destination: PAGE_ROUTE.DASHBOARD_PROFIL,
  session: {
    name: 'Sara V.',
    email: 'sara.v@izio.test',
    phone: '0755 100 200',
    avatar: 'https://i.pravatar.cc/150?img=47',
    role: 'demand',
    hasListing: true,
    profileStatus: 'active',
    slug: 'combo-pair',
    memberSince: 2026
  }
}, {
  id: 'andrei-proprietar',
  name: 'Vlad Constantin',
  role: 'supply',
  roleLabel: 'Proprietar',
  hint: '1 succes neconfirmat · 3 active · 6 oferte',
  avatar: 'https://i.pravatar.cc/150?img=12',
  destination: PAGE_ROUTE.DASHBOARD_APLICARI,
  session: {
    name: 'Vlad Constantin',
    email: 'andrei.proprietar@izio.test',
    phone: '0722 000 000',
    avatar: 'https://i.pravatar.cc/150?img=12',
    role: 'supply',
    hasListing: true,
    subRole: 'proprietar',
    memberSince: 2025,
    bio: 'Proprietar cu 2 apartamente în Floreasca și Aviației. Caut chiriași serioși pe termen lung.',
    linkedinUrl: 'https://linkedin.com/in/vlad-constantin'
  }
}, {
  id: 'maria-agent',
  name: 'Diana Popescu',
  role: 'supply',
  roleLabel: 'Agent · cu agenție',
  hint: 'Storia · 2 succese neconfirmate · stack callout',
  avatar: 'https://i.pravatar.cc/150?img=48',
  destination: PAGE_ROUTE.MARKETPLACE,
  session: {
    name: 'Diana Popescu',
    email: 'maria.agent@izio.test',
    phone: '0744 100 200',
    avatar: 'https://i.pravatar.cc/150?img=48',
    role: 'supply',
    hasListing: true,
    subRole: 'agent',
    agencyName: 'Storia Imobiliare',
    website: 'https://storia-imobiliare.ro',
    memberSince: 2024,
    bio: 'Agent senior în cadrul agenției Storia. Specializată în zonele centrale ale Bucureștiului.',
    linkedinUrl: 'https://linkedin.com/in/diana-popescu',
    facebookUrl: 'https://facebook.com/diana.popescu'
  }
}, {
  id: 'george-agent',
  name: 'Robert Marinescu',
  role: 'supply',
  roleLabel: 'Agent · solo',
  hint: 'Fără agenție · profil în lucru',
  avatar: 'https://i.pravatar.cc/150?img=13',
  destination: PAGE_ROUTE.DASHBOARD_PROFIL,
  session: {
    name: 'Robert Marinescu',
    email: 'george.agent@izio.test',
    avatar: 'https://i.pravatar.cc/150?img=13',
    role: 'supply',
    hasListing: false,
    subRole: 'agent',
    memberSince: 2026
  }
}];
interface DemoPersonasSwitcherProps {
  navigate: (page: string, context?: any) => void;
}
const DEMO_HIDDEN_KEY = 'izio.demo.switcher.hidden';
export function DemoPersonasSwitcher({
  navigate
}: DemoPersonasSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [hidden, setHidden] = useState<boolean>(() => {
    try {
      return localStorage.getItem(DEMO_HIDDEN_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [activeEmail, setActiveEmail] = useState<string | undefined>(() => getSession()?.email);
  const [verifications, setVerifications] = useState<Record<string, any>>({});
  // Keep activeEmail in sync when the panel opens.
  useEffect(() => {
    if (isOpen) {
      const email = getSession()?.email;
      setActiveEmail(email);
      if (email) {
        setVerifications(getVerificationStates(email));
      }
    }
  }, [isOpen]);
  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen]);
  // Global "H" hotkey — toggles the Demo button visibility. Useful for
  // recording clean screen captures without the floating Demo chip in shot.
  // Ignored while typing in inputs/textareas/contentEditable so it doesn't
  // hijack normal text entry.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'h' && e.key !== 'H') return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable) {
          return;
        }
      }
      setHidden(prev => {
        const next = !prev;
        try {
          if (next) {
            localStorage.setItem(DEMO_HIDDEN_KEY, '1');
          } else {
            localStorage.removeItem(DEMO_HIDDEN_KEY);
          }
        } catch {
          // noop
        }
        if (next) setIsOpen(false);
        return next;
      });
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  if (hidden) return null;
  const handleSwitch = (persona: Persona) => {
    setSession(persona.session);
    // L8 demo: seed/clear closure-ritual state per persona
    seedL8DemoFor(persona.session.email);
    setIsOpen(false);
    navigate(persona.destination);
  };
  const handleSignOut = () => {
    clearSession();
    setIsOpen(false);
    navigate(PAGE_ROUTE.HOME);
  };
  const cycleVerification = (type: 'email' | 'identity' | 'income' | 'business') => {
    if (!activeEmail) return;
    const current = verifications[type]?.status || 'unverified';
    const order: VerificationStatus[] = ['unverified', 'pending', 'verified', 'rejected', 'expired'];
    const nextIdx = (order.indexOf(current) + 1) % order.length;
    const nextStatus = order[nextIdx];
    const newState = {
      status: nextStatus,
      submittedAt: new Date().toISOString()
    };
    setVerificationState(activeEmail, type, newState);
    setVerifications(getVerificationStates(activeEmail));
  };
  const grouped = {
    demand: PERSONAS.filter(p => p.role === 'demand'),
    supply: PERSONAS.filter(p => p.role === 'supply')
  };
  return <>
      {/* Floating trigger — sits above mobile bottom nav / sticky CTAs */}
      <button type="button" onClick={() => setIsOpen(true)} className={cn('fixed z-[90] bottom-24 left-4 sm:bottom-4 sm:left-4', 'inline-flex items-center gap-2 h-10 px-3 rounded-full', 'bg-dark-2 border border-dashed border-lime-4 text-light-1', 'hover:bg-dark-3 hover:border-lime-2 transition-colors', 'shadow-lg shadow-black/30 cursor-pointer', 'outline-none focus-visible:ring-2 focus-visible:ring-lime-1')} aria-label="Deschide selectorul de personaje demo" data-id="element-1042">
        <Sparkle size={14} weight="fill" className="text-lime-1" data-id="element-1043" />
        <span className="text-xs font-medium" data-id="element-1044">Demo</span>
      </button>

      {/* Panel */}
      {isOpen && <div className="fixed inset-0 z-[100] flex items-end sm:items-center sm:justify-start" role="dialog" aria-modal="true" aria-label="Selector de personaje demo" data-id="element-1045">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-dark-1/80 backdrop-blur-sm" onClick={() => setIsOpen(false)} aria-hidden="true" data-id="element-1046" />

          {/* Panel body — bottom sheet on mobile, anchored card on desktop */}
          <div className={cn('relative w-full sm:max-w-sm bg-dark-2 border-dark-4 shadow-2xl overflow-hidden', 'sm:border sm:rounded-2xl sm:ml-4 sm:mb-4 sm:self-end', 'border-t rounded-t-2xl', 'animate-in slide-in-from-bottom-4 sm:slide-in-from-bottom-0 sm:fade-in duration-200')} data-id="element-1047">
            {/* Header */}
            <header className="flex items-center justify-between px-5 py-4 border-b border-dark-4" data-id="element-1048">
              <div className="flex items-center gap-2 min-w-0" data-id="element-1049">
                <div className="w-7 h-7 rounded-md bg-lime-5 border border-lime-4 inline-flex items-center justify-center shrink-0" data-id="element-1050">
                  <Sparkle size={14} weight="fill" className="text-lime-1" data-id="element-1051" />
                </div>
                <div className="min-w-0" data-id="element-1052">
                  <h2 className="text-sm font-semibold text-light-1 leading-tight" data-id="element-1053">
                    Demo personas
                  </h2>
                  <p className="text-[10px] text-light-3 uppercase tracking-wider mt-0.5" data-id="element-1054">
                    Doar pentru prototip
                  </p>
                </div>
              </div>
              <button onClick={() => setIsOpen(false)} className="text-light-3 hover:text-light-1 transition-colors p-1 -m-1 cursor-pointer rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-lime-1" aria-label="Închide" data-id="element-1055">
                <X size={18} weight="bold" data-id="element-1056" />
              </button>
            </header>

            <div className="max-h-[70vh] sm:max-h-[600px] overflow-y-auto" data-id="element-1057">
              {/* Demand group */}
              <PersonaGroup icon={<House size={12} weight="fill" className="text-light-3" data-id="element-1059" />} label="Chiriași (demand)" data-id="element-1058" />
              <ul className="divide-y divide-dark-4" data-id="element-1060">
                {grouped.demand.map(p => <PersonaRow key={p.id} persona={p} isActive={activeEmail === p.session.email} onClick={() => handleSwitch(p)} data-id="element-1061" />)}
              </ul>

              {/* Supply group */}
              <PersonaGroup icon={<Buildings size={12} weight="fill" className="text-light-3" data-id="element-1063" />} label="Proprietari & agenți (supply)" data-id="element-1062" />
              <ul className="divide-y divide-dark-4" data-id="element-1064">
                {grouped.supply.map(p => <PersonaRow key={p.id} persona={p} isActive={activeEmail === p.session.email} onClick={() => handleSwitch(p)} data-id="element-1065" />)}
              </ul>

              {/* Dev Tools: Verifications */}
              {activeEmail && <div className="px-5 py-4 border-t border-dark-4 bg-dark-2/50" data-id="element-1066">
                  <div className="flex items-center gap-1.5 mb-3" data-id="element-1067">
                    <Wrench size={12} weight="fill" className="text-light-3" data-id="element-1068" />
                    <span className="text-[10px] font-medium text-light-3 uppercase tracking-wider" data-id="element-1069">
                      Dev: Status verificări
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2" data-id="element-1070">
                    {(['email', 'identity', 'income', 'business'] as const).map(type => {
                const status = verifications[type]?.status || 'unverified';
                return <button key={type} type="button" onClick={() => cycleVerification(type)} className="h-8 px-2 rounded bg-dark-3 border border-dark-4 text-left flex justify-between items-center hover:border-light-4 transition-colors cursor-pointer" data-id="element-1071">
                            <span className="text-xs capitalize text-light-2" data-id="element-1072">
                              {type}
                            </span>
                            <span className={cn('text-[10px] px-1.5 py-0.5 rounded font-medium', status === 'verified' ? 'bg-lime-5/20 text-lime-1' : status === 'pending' ? 'bg-amber-500/20 text-amber-400' : status === 'rejected' ? 'bg-red-500/20 text-red-400' : status === 'expired' ? 'bg-orange-500/20 text-orange-400' : 'bg-dark-4 text-light-4')} data-id="element-1073">
                              {status}
                            </span>
                          </button>;
              })}
                  </div>
                  <p className="text-[10px] text-light-4 mt-2 leading-relaxed" data-id="element-1074">
                    Apasă pentru a cicla statusul. După modificare, dă refresh
                    la pagină (F5) sau navighează pentru a vedea schimbările în
                    UI.
                  </p>
                </div>}

              {/* Sign out */}
              <div className="px-5 py-4 border-t border-dark-4" data-id="element-1075">
                <button type="button" onClick={handleSignOut} className="w-full h-10 rounded-md bg-dark-3 hover:bg-dark-4 text-light-2 hover:text-light-1 font-medium text-xs inline-flex items-center justify-center gap-2 border border-dark-4 transition-colors cursor-pointer" data-id="element-1076">
                  <SignIn size={14} weight="bold" data-id="element-1077" />
                  Delogare (vizitator)
                </button>
              </div>
            </div>
          </div>
        </div>}
    </>;
}
function PersonaGroup({
  icon,
  label
}: {
  icon: React.ReactNode;
  label: string;
}) {
  return <div className="flex items-center gap-1.5 px-5 pt-4 pb-2" data-id="element-1078">
      {icon}
      <span className="text-[10px] font-medium text-light-3 uppercase tracking-wider" data-id="element-1079">
        {label}
      </span>
    </div>;
}
function PersonaRow({
  persona,
  isActive,
  onClick
}: {
  persona: Persona;
  isActive: boolean;
  onClick: () => void;
}) {
  return <li data-id="element-1080">
      <button type="button" onClick={onClick} className={cn('w-full flex items-center gap-3 px-5 py-3 text-left transition-colors outline-none cursor-pointer', isActive ? 'bg-lime-5 hover:bg-lime-4' : 'hover:bg-dark-3 focus-visible:bg-dark-3')} data-id="element-1081">
        <img src={persona.avatar} alt={persona.name} className="w-10 h-10 rounded-full object-cover shrink-0" data-id="element-1082" />
        <div className="min-w-0 flex-1" data-id="element-1083">
          <div className="flex items-center gap-1.5" data-id="element-1084">
            <span className="text-sm font-medium text-light-1 truncate" data-id="element-1085">
              {persona.name}
            </span>
            {isActive && <Check size={12} weight="bold" className="text-lime-1 shrink-0" aria-label="Activ" data-id="element-1086" />}
          </div>
          <div className="text-[11px] text-light-3 mt-0.5 truncate" data-id="element-1087">
            <span className="text-light-2" data-id="element-1088">{persona.roleLabel}</span>
            <span className="text-light-4" data-id="element-1089"> · </span>
            <span data-id="element-1090">{persona.hint}</span>
          </div>
        </div>
        <CaretRight size={14} weight="bold" className="text-light-3 shrink-0" data-id="element-1091" />
      </button>
    </li>;
}